import ChevronDown from '@spectrum-icons/workflow/ChevronDown';

import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { formatNumber } from '../cardStats/formatters';
import { renderManaText } from '../cardStats/manaText';
import { Flag } from '../tags/Tags';
import { scoringEntryView } from './cardScoring';
import { ChipList, Section, TagList } from './Section';

function DetailList({ details }) {
    if (details.length === 0) return null;
    return (
        <dl className="cardInfoDataList">
            {details.map((detail) => (
                <div className="cardInfoDataRow" key={detail.key} title={detail.hint}>
                    <dt>{detail.label}</dt>
                    <dd>{detail.value}</dd>
                </div>
            ))}
        </dl>
    );
}

function ScoringEntry({ entry, context }) {
    const view = scoringEntryView(entry, context);
    const hasBreakdown = view.details.length > 0 || view.sections.length > 0 || view.sourceLine;
    return (
        <div className="cardInfoEntry">
            <p className="cardInfoEntryHead">
                <span className="cardInfoEntryEffect">{view.effectLabel}</span>
                <span className="cardInfoEntryScore">{formatNumber(view.score, 1)}</span>
            </p>
            {view.triggerText && (
                <p className="cardInfoQuote">
                    <span className="cardInfoQuoteTag">Fires on</span> &ldquo;
                    {renderManaText(view.triggerText)}&rdquo;
                </p>
            )}
            {view.resultText && (
                <p className="cardInfoQuote">
                    <span className="cardInfoQuoteTag">{view.resultLabel}</span> &ldquo;
                    {renderManaText(view.resultText)}&rdquo;
                </p>
            )}
            <TagList tags={view.chips} />
            {view.badges.length > 0 && (
                <ul className="cardInfoBadges">
                    {view.badges.map((badge) => (
                        <li key={badge.key}>
                            <Flag tone={badge.tone} title={badge.hint}>
                                {badge.label}
                            </Flag>
                        </li>
                    ))}
                </ul>
            )}
            {hasBreakdown && (
                <details className="cardInfoDetails">
                    <summary className="cardInfoDetailsSummary">
                        Score breakdown
                        <ChevronDown size="XS" />
                    </summary>
                    <DetailList details={view.details} />
                    {view.sections.map((section) => (
                        <div className="cardInfoSubsection" key={section.key}>
                            <p className="cardInfoSubsectionTitle">{section.title}</p>
                            <DetailList details={section.details || []} />
                            <TagList tags={section.pips || []} />
                            <ChipList items={section.chips || []} />
                        </div>
                    ))}
                    {view.sourceLine && (
                        <p className="cardInfoSourceLine">
                            Matched against &ldquo;{renderManaText(view.sourceLine)}&rdquo;
                        </p>
                    )}
                </details>
            )}
        </div>
    );
}

/**
 * What each role scored and why. Every entry traces back to the rules text
 * that produced it; the arithmetic behind the score sits one disclosure away
 * so the flow stays readable.
 */
export function CardScoringSection({ scoring, context }) {
    const categories = Object.keys(scoring || {});
    if (categories.length === 0) return null;
    return (
        <Section
            title="Category scores"
            hint="How this card scores within each role. Cards can have several entries per category when more than one effect qualifies."
        >
            {categories.map((category) => {
                const entries = scoring[category] || [];
                return (
                    <article className="cardInfoCategory" key={category}>
                        <h4 className="cardInfoCategoryName">
                            {getPrettyLabel(category)}
                            <span className="cardInfoCategoryCount">
                                {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
                            </span>
                        </h4>
                        {entries.map((entry, idx) => (
                            <ScoringEntry
                                entry={entry}
                                context={context}
                                key={`${entry.contextIds?.condition || idx}-${idx}`}
                            />
                        ))}
                    </article>
                );
            })}
        </Section>
    );
}
